import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";

const Dashboard = () => {
  const [totalDonations, setTotalDonations] = useState(0);
  const [totalMembers, setTotalMembers] = useState(0);
  const [paymentStats, setPaymentStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch("https://mosque-backend-yfae.vercel.app/allDonation")
      .then((res) => res.json())
      .then((donations) => {
        setTotalMembers(donations.length);

        const total = donations.reduce(
          (sum, item) => sum + Number(item.amount || 0),
          0,
        );
        setTotalDonations(total);

        const paymentData = donations.reduce((acc, item) => {
          const method = item.paymentMethod || "Unknown";
          acc[method] = (acc[method] || 0) + Number(item.amount || 0);
          return acc;
        }, {});
        setPaymentStats(paymentData);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching donations:", error);
        setIsLoading(false);
      });
  }, []);

  const pieData = Object.keys(paymentStats).map((key) => ({
    name: key,
    value: paymentStats[key],
  }));

  // Premium Spiritual/Modern Color Palette
  const COLORS = ["#047857", "#f59e0b", "#10b981", "#334155", "#64748b"];

  // Helper for clean formatting of BDT currency values
  const formatCurrency = (value) => {
    return (
      new Intl.NumberFormat("en-BD", {
        maximumFractionDigits: 0,
      }).format(value) + " ৳"
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-emerald-800/30 border-t-emerald-800 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Heading and Context Badge */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-5 border-b border-slate-200">
        <div>
          <h1 className="text-3xl font-serif font-bold text-slate-800 tracking-wide">
            Donation Overview
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Real-time balance processing metrics & community interaction
            statistics
          </p>
        </div>
        <div className="self-start sm:self-center px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full flex items-center gap-2 text-xs font-semibold text-emerald-800">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          Live Metrics
        </div>
      </div>

      {/* Top Level Metric Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Members Metric Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-5">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-700 shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
              />
            </svg>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Total Members
            </p>
            <h3 className="text-3xl font-bold text-slate-800 mt-1">
              {totalMembers}
            </h3>
          </div>
        </div>

        {/* Aggregate Balance Metric Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-5 md:col-span-2">
          <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 7v10m3-7H9"
              />
            </svg>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Total Gathered Funds
            </p>
            <h3 className="text-3xl font-bold text-slate-800 mt-1">
              {formatCurrency(totalDonations)}
            </h3>
          </div>
        </div>
      </div>

      {/* Graphical Data Visualization Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Total Donations Bar Representation */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm lg:col-span-2 flex flex-col">
          <h3 className="text-md font-bold text-slate-800 tracking-tight mb-6">
            Aggregate Pool
          </h3>
          <div className="flex-1 min-h-[300px] flex items-end">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={[{ name: "Total Funds", amount: totalDonations }]}
                margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f1f5f9"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: "#f8fafc" }}
                  formatter={(value) => [formatCurrency(value), "Amount"]}
                />
                <Bar
                  dataKey="amount"
                  fill="#047857"
                  radius={[10, 10, 0, 0]}
                  barSize={55}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Payment Methods Distribution Donut Chart */}
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm lg:col-span-3 flex flex-col">
          <h3 className="text-md font-bold text-slate-800 tracking-tight mb-6">
            Gateway Stream Channeling
          </h3>
          <div className="flex-1 min-h-[300px]">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={65}
                  outerRadius={95}
                  paddingAngle={4}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [formatCurrency(value), "Contribution"]}
                />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: "13px", paddingTop: "10px" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
